import { useEffect, useContext, useState, useRef } from "react";
import { FormProvider, useForm, Controller, useWatch } from "react-hook-form";
import { Button, Col, Form, Row, Image } from "react-bootstrap";
import GlobalContext from "../../context/GlobalContext";
import swal from "../../services/swalConfig";

import SelectCountry from "../../components/select/SelectCountry";
import SelectState from "../../components/select/SelectState";
import SelectCity from "../../components/select/SelectCity";
import SelectCompanyType from "../../components/select/SelectCompanyType";
import SelectCurrency from "../../components/select/SelectCurrency";
import NoLogo from "../../assets/images/icon/no-logo.svg";

import { getSettings, updateCompany } from "../../api/CompanyApi";
import { userProfile } from "../../api/userProfileApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

import { tr } from "../../i18n";
const SettingsPage = () => {
  const { user, syncUserState, updateActiveWorkspace, setLoading } =
    useContext(GlobalContext);
  const [logoPreview, setLogoPreview] = useState(null);
  const [payrollLogoPreview, setPayrollLogoPreview] = useState(null);

  const companyLogoRef = useRef(null);
  const payrollLogoRef = useRef(null);

  const methods = useForm({
    defaultValues: {
      company_id: "",
      company_name: "",
      company_description: "",
      company_address: "",
      company_phone: "",
      company_email: "",
      company_tax_id: "",
      company_website: "",
      company_linkedin: "",
      company_type: "",
      currency: "",
      country_id: "",
      state_id: "",
      city_id: "",
      default_paid_leave: "0",
      auto_renewal_paid_leave: false,
      renewal_paid_leave_effect: "decrease",
      renewal_paid_leave_method: "fixed_date",
      renewal_month: "01",
      renewal_day: "01",
      company_type_default: null,
      currency_default: null,
      country_default: null,
      state_default: null,
      city_default: null,
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    register,
    setValue,
    setError,
    formState: { errors },
  } = methods;

  // Memantau field secara spesifik untuk menghindari re-render masif
  const watchAutoRenewal = useWatch({
    control,
    name: "auto_renewal_paid_leave",
  });
  const watchRenewalMethod = useWatch({
    control,
    name: "renewal_paid_leave_method",
  });
  const watchCountryId = useWatch({ control, name: "country_id" });
  const watchStateId = useWatch({ control, name: "state_id" });

  // PERBAIKAN: Watch defaultValue fields agar reactive saat di-clear
  const watchCountryDefault = useWatch({ control, name: "country_default" });
  const watchStateDefault = useWatch({ control, name: "state_default" });
  const watchCityDefault = useWatch({ control, name: "city_default" });
  const watchCompanyTypeDefault = useWatch({
    control,
    name: "company_type_default",
  });
  const watchCurrencyDefault = useWatch({ control, name: "currency_default" });
  const watchCompanyIdValue = useWatch({ control, name: "company_id" });

  /** Load company info */
  const companyId = user?.workspace?.company_id;

  useEffect(() => {
    const loadCompany = async () => {
      if (!companyId) return;

      setLoading(true);
      try {
        const result = await getSettings(companyId);
        if (result.status === 200) {
          const data = result.data;

          if (data.renewal_paid_leave_date) {
            const [month, day] = data.renewal_paid_leave_date.split("-");
            data.renewal_month = month;
            data.renewal_day = day;
          }

          // ============ PERBAIKAN UTAMA ============
          // Konversi string "true"/"false" menjadi boolean
          const autoRenewalValue =
            data.auto_renewal_paid_leave === "true" ||
            data.auto_renewal_paid_leave === true;

          const formMappedData = {
            ...data,
            // Konversi eksplisit ke boolean
            auto_renewal_paid_leave: autoRenewalValue,
            default_paid_leave: data.default_paid_leave || "0",
            country_default: { id: data.country_id, text: data.country_name },
            state_default: { id: data.state_id, text: data.state_name },
            city_default: { id: data.city_id, text: data.city_name },
            company_type_default: {
              id: data.company_type,
              text: data.company_type_text,
            },
            currency_default: {
              id: data.currency,
              text: data.currency_text,
            },
          };

          reset(formMappedData);
          setLogoPreview(data.company_logo);
          setPayrollLogoPreview(data.payroll_logo);
        }
      } catch (error) {
        console.error("Load Error:", error);
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
  }, [companyId, reset, setLoading]);

  /** Handle submit */
  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();

      const allowedKeys = [
        "company_id",
        "company_name",
        "company_description",
        "company_address",
        "company_phone",
        "company_email",
        "company_tax_id",
        "company_website",
        "company_linkedin",
        "company_type",
        "country_id",
        "state_id",
        "city_id",
        "currency",
        "default_paid_leave",
        "auto_renewal_paid_leave",
        "renewal_paid_leave_effect",
        "renewal_paid_leave_method",
        "renewal_month",
        "renewal_day",
        "payroll_name",
        "company_logo",
        "payroll_logo",
      ];

      allowedKeys.forEach((key) => {
        if (data[key] === undefined || data[key] === null) {
          return;
        }

        if (typeof data[key] === "boolean") {
          formData.append(key, data[key] ? "true" : "false");
          return;
        }

        formData.append(key, data[key]);
      });

      const response = await updateCompany(formData);

      if (response.status === 200) {
        const updatedProfile = await userProfile({ company_id: companyId });
        if (updatedProfile?.data) {
          syncUserState(updatedProfile.data);
          updateActiveWorkspace({
            company_name:
              data.company_name || updatedProfile.data?.workspace?.company_name,
            company_logo:
              response?.data?.company_logo ||
              updatedProfile.data?.workspace?.company_logo,
          });
          setLogoPreview(
            response?.data?.company_logo ||
              updatedProfile.data?.workspace?.company_logo,
          );
        }
        swal.fire(tr("shared:success_2"), response.message, "success");
      } else if (response.status === 400) {
        Object.keys(response.message).forEach((field) => {
          setError(field, { type: "server", message: response.message[field] });
        });
        swal.fire(tr("settings:validation_error"), tr("settings:please_check_the_form"), "error");
      }
    } catch (error) {
      swal.fire(tr("shared:error"), tr("settings:something_went_wrong"), "error");
    } finally {
      setLoading(false);
    }
  });

  const handleImageChange = (e, setter, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setValue(fieldName, file, { shouldValidate: true });

    const reader = new FileReader();
    reader.onloadend = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit}>
        <input type="hidden" {...register("company_id", { required: true })} />

        <Row>
          <Col lg={8}>
            <div className="box-shadow p-4 bg-white rounded">
              <h5 className="mb-4">{tr("settings:company_information")}</h5>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:company_id")}</Form.Label>
                    <Form.Control value={watchCompanyIdValue || "-"} disabled />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:company_name")}</Form.Label>
                    <Form.Control
                      {...register("company_name", { required: tr("settings:required") })}
                      isInvalid={!!errors.company_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("shared:description")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder={tr("settings:decribe_your_company_here")}
                      {...register("company_description")}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:address")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      {...register("company_address", { required: tr("settings:required") })}
                      isInvalid={!!errors.company_address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_address?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:company_phone")}</Form.Label>
                    <Form.Control
                      {...register("company_phone", { required: tr("settings:required") })}
                      isInvalid={!!errors.company_phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_phone?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:company_email")}</Form.Label>
                    <Form.Control
                      type="email"
                      {...register("company_email", { required: tr("settings:required") })}
                      isInvalid={!!errors.company_email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:tax_id")}</Form.Label>
                    <Form.Control {...register("company_tax_id")} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:website")}</Form.Label>
                    <Form.Control
                      placeholder="https://example.com"
                      {...register("company_website")}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:linkedin")}</Form.Label>
                    <Form.Control
                      placeholder="https://linkedin.com/company/..."
                      {...register("company_linkedin")}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:company_type")}</Form.Label>
                    <Controller
                      name="company_type"
                      control={control}
                      rules={{ required: tr("settings:required") }}
                      render={({ field }) => (
                        <SelectCompanyType
                          {...field}
                          defaultValue={watchCompanyTypeDefault}
                          className={errors.company_type ? "is-invalid" : ""}
                          onChange={(val) => {
                            field.onChange(val?.value || val?.id || val || "");
                            setValue("company_type_default", null);
                          }}
                        />
                      )}
                    />
                    {errors.company_type && (
                      <div className="text-danger small mt-1">
                        {errors.company_type.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:currency")}</Form.Label>
                    <Controller
                      name="currency"
                      control={control}
                      rules={{ required: tr("settings:required") }}
                      render={({ field }) => (
                        <SelectCurrency
                          {...field}
                          defaultValue={watchCurrencyDefault}
                          className={errors.currency ? "is-invalid" : ""}
                          onChange={(val) => {
                            field.onChange(val?.value || val?.id || val || "");
                            setValue("currency_default", null);
                          }}
                        />
                      )}
                    />
                    {errors.currency && (
                      <div className="text-danger small mt-1">
                        {errors.currency.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="mt-3 mb-3">{tr("shared:location")}</h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:country")}</Form.Label>
                    <Controller
                      name="country_id"
                      control={control}
                      rules={{ required: tr("settings:required") }}
                      render={({ field }) => (
                        <SelectCountry
                          {...field}
                          defaultValue={watchCountryDefault}
                          className={errors.country_id ? "is-invalid" : ""}
                          onChange={(val) => {
                            const value = val?.value || val?.id || val || "";
                            field.onChange(value);
                            setValue("country_default", null);
                            setValue("state_id", "");
                            setValue("city_id", "");
                            setValue("state_default", null);
                            setValue("city_default", null);
                          }}
                        />
                      )}
                    />
                    {errors.country_id && (
                      <div className="text-danger small mt-1">
                        {errors.country_id.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:state")}</Form.Label>
                    <Controller
                      name="state_id"
                      control={control}
                      rules={{ required: tr("settings:required") }}
                      render={({ field }) => (
                        <SelectState
                          {...field}
                          country_id={watchCountryId}
                          defaultValue={watchStateDefault}
                          className={errors.state_id ? "is-invalid" : ""}
                          onChange={(val) => {
                            const value = val?.value || val?.id || val || "";
                            field.onChange(value);
                            setValue("state_default", null);
                            setValue("city_id", "");
                            setValue("city_default", null);
                          }}
                        />
                      )}
                    />
                    {errors.state_id && (
                      <div className="text-danger small mt-1">
                        {errors.state_id.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{tr("settings:city")}</Form.Label>
                    <Controller
                      name="city_id"
                      control={control}
                      rules={{ required: tr("settings:required") }}
                      render={({ field }) => (
                        <SelectCity
                          {...field}
                          state_id={watchStateId}
                          defaultValue={watchCityDefault}
                          className={errors.city_id ? "is-invalid" : ""}
                          onChange={(val) => {
                            field.onChange(val?.value || val?.id || val || "");
                            setValue("city_default", null);
                          }}
                        />
                      )}
                    />
                    {errors.city_id && (
                      <div className="text-danger small mt-1">
                        {errors.city_id.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      {tr("settings:default_paid_leave")}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      {...register("default_paid_leave", {
                        required: tr("settings:required"),
                      })}
                      isInvalid={!!errors.default_paid_leave}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.default_paid_leave?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Leave Renewal Section */}
              <div className="mt-4 p-3 border rounded bg-light">
                <Controller
                  name="auto_renewal_paid_leave"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      label={tr("settings:enable_auto_renewal_paid_leave")}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />

                {watchAutoRenewal && (
                  <div className="mt-3 p-3 border rounded bg-white">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            {tr("settings:renewal_effect")}
                          </Form.Label>
                          <select
                            {...register("renewal_paid_leave_effect")}
                            className="form-select"
                            defaultValue=""
                          >
                            <option value="" disabled>
                              {tr("settings:choose_how_the_renewal_affects_employee")}
                            </option>
                            <option value="increase">
                              {tr("settings:increase_add_to_balance")}
                            </option>
                            <option value="decrease">
                              {tr("settings:decrease_reset_reduce_balance")}
                            </option>
                          </select>
                          <small className="text-muted d-block mt-1">
                            {errors.renewal_paid_leave_effect?.message}
                          </small>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            {tr("settings:renewal_method")}
                          </Form.Label>
                          <Form.Select
                            {...register("renewal_paid_leave_method")}
                            defaultValue=""
                          >
                            <option value="" disabled>
                              {tr("settings:select_renewal_method")}
                            </option>
                            <option value="fixed_date">{tr("settings:fixed_date")}</option>
                            <option value="employee_join_date">
                              {tr("shared:join_date")}
                            </option>
                          </Form.Select>
                          <small className="text-muted d-block mt-1">
                            {errors.renewal_paid_leave_method?.message}
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>

                    {watchRenewalMethod === "fixed_date" && (
                      <Row>
                        <Col md={6}>
                          <Form.Label className="fw-bold">{tr("settings:month")}</Form.Label>
                          <Form.Select {...register("renewal_month")}>
                            <option value="01">{tr("settings:january")}</option>
                            <option value="02">{tr("settings:february")}</option>
                            <option value="03">{tr("settings:march")}</option>
                            <option value="04">{tr("settings:april")}</option>
                            <option value="05">{tr("settings:may")}</option>
                            <option value="06">{tr("settings:june")}</option>
                            <option value="07">{tr("settings:july")}</option>
                            <option value="08">{tr("settings:august")}</option>
                            <option value="09">{tr("settings:september")}</option>
                            <option value="10">{tr("settings:october")}</option>
                            <option value="11">{tr("settings:november")}</option>
                            <option value="12">{tr("settings:december")}</option>
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="fw-bold">{tr("shared:day")}</Form.Label>
                          <Form.Select {...register("renewal_day")}>
                            {Array.from({ length: 31 }, (_, i) => (
                              <option
                                key={i}
                                value={String(i + 1).padStart(2, "0")}
                              >
                                {i + 1}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end mt-4">
                <Button type="submit" variant="primary" size="sm">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  {tr("settings:save_changes")}
                </Button>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            {/* Logo Section */}
            <div className="box-shadow p-4 bg-white rounded mb-4 text-center">
              <Form.Label className="fw-bold d-block mb-3">
                {tr("settings:company_logo")}
              </Form.Label>
              <div
                className="image-upload-wrapper mx-auto"
                onClick={() => companyLogoRef.current.click()}
              >
                <Image
                  src={logoPreview || NoLogo}
                  className="img-preview-rect shadow-sm"
                />
                <div className="overlay">
                  <span>{tr("settings:choose_picture")}</span>
                </div>
              </div>
              <input
                type="file"
                ref={companyLogoRef}
                className="d-none"
                accept="image/*"
                onChange={(e) =>
                  handleImageChange(e, setLogoPreview, "company_logo")
                }
              />
              <small className="text-muted d-block mt-2">
                {tr("settings:recommended_square_size_max_2mb")}
              </small>
            </div>

            {/* Payroll Section */}
            <div className="box-shadow p-4 bg-white rounded border-primary border-top">
              <h6 className="fw-bold mb-3 text-primary">{tr("settings:payroll_settings")}</h6>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">{tr("shared:payroll_name")}</Form.Label>
                <input
                  {...register("payroll_name")}
                  className="form-control form-control-sm"
                  placeholder={tr("settings:custom_payroll_name")}
                />
                <small className="text-info" style={{ fontSize: "10px" }}>
                  {tr("settings:leave_blank_to_use_default_company_2")}
                </small>
              </Form.Group>

              <Form.Group className="text-center">
                <Form.Label className="small fw-bold d-block text-start">
                  {tr("settings:payroll_logo")}
                </Form.Label>
                <div
                  className="image-upload-wrapper mx-auto small-preview"
                  onClick={() => payrollLogoRef.current.click()}
                >
                  <Image
                    src={payrollLogoPreview || NoLogo}
                    className="img-preview-rect shadow-sm"
                  />
                  <div className="overlay">
                    <span>{tr("settings:choose")}</span>
                  </div>
                </div>
                <input
                  type="file"
                  ref={payrollLogoRef}
                  className="d-none"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageChange(e, setPayrollLogoPreview, "payroll_logo")
                  }
                />
                <div
                  className="mt-2 text-info"
                  style={{ fontSize: "10px", lineHeight: "1.2" }}
                >
                  {tr("settings:leave_blank_to_use_default_company")}
                </div>
              </Form.Group>
            </div>
          </Col>
        </Row>
      </Form>

      <style jsx>{`
        .image-upload-wrapper {
          position: relative;
          width: 150px;
          height: 150px;
          cursor: pointer;
          border: 2px dashed #ddd;
          border-radius: 8px;
          overflow: hidden;
        }
        .img-preview-rect {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .overlay {
          position: absolute;
          top: 0;
          background: rgba(0, 0, 0, 0.5);
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          opacity: 0;
          transition: 0.3s;
        }
        .image-upload-wrapper:hover .overlay {
          opacity: 1;
        }
      `}</style>
    </FormProvider>
  );
};

export default SettingsPage;
